import { ReloadOutlined, SafetyCertificateOutlined, SolutionOutlined } from '@ant-design/icons';
import { Alert, Button, Skeleton, Space, Typography, message, theme } from 'antd';
import { CardHeader, PageHeader } from 'components/headings';
import { descargarMisDatos, getMisDatos } from 'api/me';
import AparicionesPanel from './AparicionesPanel';
import CuentaPanel from './CuentaPanel';
import SolicitudModal from './SolicitudModal';
import React, { useCallback, useEffect, useState } from 'react';

const { Text } = Typography;
const { useToken } = theme;

/**
 * "Mis datos" — donde cualquier persona con cuenta ejerce sus derechos sobre sus
 * datos personales (Ley 21.719).
 *
 * Tres de los seis derechos se resuelven en esta misma pantalla: ver (el panel de
 * apariciones), corregir (el form de la cuenta) y descargar (portabilidad). Los
 * otros tres pasan por el modal de solicitud, porque necesitan que alguien evalúe
 * si hay obligación legal de conservar.
 */
const MisDatosPage = () => {
  const { token } = useToken();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [solicitudAbierta, setSolicitudAbierta] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(false);
    try {
      setDatos(await getMisDatos());
    } catch {
      setError(true);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const descargar = async () => {
    setDescargando(true);
    try {
      await descargarMisDatos();
      message.success('Descargamos tu archivo. Revisa las descargas de tu navegador.');
    } catch {
      message.error('No pudimos preparar tu archivo. Inténtalo de nuevo.');
    } finally {
      setDescargando(false);
    }
  };

  // La cuenta que devuelve el PUT ya viene actualizada: la usamos en vez de
  // recargar toda la página, que traería de nuevo el resumen sin necesidad.
  const onCuentaActualizada = (cuenta) => setDatos((previo) => ({ ...previo, cuenta }));

  const panelStyle = {
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
    overflow: 'hidden',
    background: token.colorBgContainer,
    boxShadow: token.boxShadowSecondary,
  };

  const wrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: token.marginLG,
    paddingTop: token.paddingMD,
    paddingBottom: token.paddingXL,
  };

  if (error) {
    return (
      <div style={{ paddingTop: token.paddingLG }}>
        <Alert
          message="No pudimos cargar tus datos"
          description="Hubo un problema al buscar tu información. Vuelve a intentarlo en unos segundos."
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={cargar}>
              Reintentar
            </Button>
          }
        />
      </div>
    );
  }

  if (cargando || !datos) {
    return (
      <div style={wrapperStyle}>
        <PageHeader
          icon={SolutionOutlined}
          title="Mis datos"
          subtitle="Consulta, corrige y decide sobre la información que guardamos de ti"
        />
        <div style={panelStyle}>
          <div style={{ padding: token.paddingMD }}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        </div>
        <div style={panelStyle}>
          <div style={{ padding: token.paddingMD }}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <PageHeader
        icon={SolutionOutlined}
        title="Mis datos"
        subtitle="Consulta, corrige y decide sobre la información que guardamos de ti"
        actions={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={cargar}>
              Actualizar
            </Button>
            <Button
              type="primary"
              icon={<SafetyCertificateOutlined />}
              onClick={() => setSolicitudAbierta(true)}
            >
              Pedir algo sobre tus datos
            </Button>
          </Space>
        }
      />

      <CuentaPanel
        cuenta={datos.cuenta}
        onActualizado={onCuentaActualizada}
        panelStyle={panelStyle}
      />

      <AparicionesPanel
        resumen={datos.resumen}
        totalRegistros={datos.totalRegistros}
        firmas={datos.firmas}
        onDescargar={descargar}
        descargando={descargando}
        panelStyle={panelStyle}
      />

      <div style={panelStyle}>
        <CardHeader
          icon={SafetyCertificateOutlined}
          tint={token.colorWarning}
          title="Pedir algo sobre tus datos"
          subtitle="Cuando quieres que borremos, congelemos o dejemos de usar tu información"
          action={
            <Button size="small" onClick={() => setSolicitudAbierta(true)}>
              Hacer una petición
            </Button>
          }
        />
        <div style={{ padding: token.paddingMD }}>
          <Text type="secondary">
            Puedes pedirnos que borremos tus datos, que dejemos de usarlos para algo en concreto o
            que los congelemos mientras revisamos tu caso. Estas peticiones no son automáticas:
            alguien del equipo las revisa, porque hay registros de trabajos que debemos conservar
            por ley.
          </Text>
        </div>
      </div>

      <SolicitudModal open={solicitudAbierta} onClose={() => setSolicitudAbierta(false)} />
    </div>
  );
};

export default MisDatosPage;
